import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useCallback,
} from "react";

const PopupStackContext = createContext();

export const usePopupStack = () => useContext(PopupStackContext);

export const PopupStackProvider = ({ children }) => {
    const stackRef = useRef([]); // holds { onClose } objects

    // Stable push / remove that ONLY mutate the ref – no re-render
    const push = useCallback((popup) => {
        stackRef.current.push(popup);
    }, []);

    const remove = useCallback((popup) => {
        stackRef.current = stackRef.current.filter((p) => p !== popup);
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape" || e.key === "Esc") {
                const stack = stackRef.current;
                const topPopup = stack[stack.length - 1];

                if (topPopup && typeof topPopup.onClose === "function") {
                    topPopup.onClose();
                    // Do NOT call remove here; let the popup unmount
                    // and its own useStackedPopup cleanup will call remove().
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

    return (
        <PopupStackContext.Provider value={{ push, remove }}>
            {children}
        </PopupStackContext.Provider>
    );
};

/**
 * Hook to register a popup on the global ESC stack.
 *
 * - `isOpen`: whether the popup is visible
 * - `onClose`: what to do when ESC hits this popup
 */
export const useStackedPopup = (isOpen, onClose) => {
    const { push, remove } = usePopupStack();
    const popupRef = useRef(null);

    useEffect(() => {
        if (!isOpen || !onClose) {
            // If closing, cleanup any registration
            if (popupRef.current) {
                remove(popupRef.current);
                popupRef.current = null;
            }
            return;
        }

        const popup = { onClose };
        popupRef.current = popup;
        push(popup);

        return () => {
            if (popupRef.current) {
                remove(popupRef.current);
                popupRef.current = null;
            }
        };
    }, [isOpen, onClose, push, remove]);
};
